// src/pages/admin/AdminReports.jsx
import React, { useState, useEffect } from 'react';
import {
  Search,
  Eye,
  CheckCircle,
  XCircle,
  Clock,
  AlertTriangle,
  Flag,
  User,
  Calendar,
  RefreshCw,
  FileText,
  MessageCircle
} from 'lucide-react';
import styles from '../../styles/ReportsPage.module.css';
import { toast } from 'react-hot-toast';
import useTranslation from '../../hooks/useTranslation';
import { useDispatch, useSelector } from 'react-redux';
import {
  getAllReports,
  getReportById,
  assignReportToAdmin,
  updateReportStatus,
  addAdminResponse,
  resolveReport,
  dismissReport,
  getReportStatistics,
  getUrgentCount,
  escalateReport,
  selectReports,
  selectCurrentReport,
  selectReportPagination,
  selectReportsLoading,
  selectReportUpdating,
  selectReportStatistics,
  selectUrgentCount,
  selectReportError,
  selectUpdateReportError,
  clearReportErrors,
  clearReportSuccess,
  setReportFilters,
} from '../../store/slices/reportSlice';
import {
  selectCurrentUser,
  selectIsAuthenticated
} from '../../store/slices/adminSlice';

const AdminReports = () => {
  const dispatch = useDispatch();
  
  // Redux state - Report
  const reports = useSelector(selectReports);
  const currentReport = useSelector(selectCurrentReport);
  const pagination = useSelector(selectReportPagination);
  const loading = useSelector(selectReportsLoading);
  const updating = useSelector(selectReportUpdating);
  const statistics = useSelector(selectReportStatistics);
  const urgentCount = useSelector(selectUrgentCount);
  const error = useSelector(selectReportError);
  const updateError = useSelector(selectUpdateReportError);
  
  // Redux state - Admin
  const currentUser = useSelector(selectCurrentUser);
  const isAuthenticated = useSelector(selectIsAuthenticated);
  
  // Local state
  const [selectedReport, setSelectedReport] = useState(null);
  const [filter, setFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [showDetails, setShowDetails] = useState(false);
  const [responseMessage, setResponseMessage] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [limit] = useState(10);
  const [actionType, setActionType] = useState('response');
  const [isProcessing, setIsProcessing] = useState(false);

  // Translation hooks
  const { translatedText: reportsLabel } = useTranslation('Reports');
  const { translatedText: total } = useTranslation('Total');
  const { translatedText: pending } = useTranslation('Pending');
  const { translatedText: inProgress } = useTranslation('In Progress');
  const { translatedText: resolved } = useTranslation('Resolved');
  const { translatedText: urgent } = useTranslation('Urgent');
  const { translatedText: refresh } = useTranslation('Refresh');
  const { translatedText: allReports } = useTranslation('All Reports');
  const { translatedText: underReview } = useTranslation('Under Review');
  const { translatedText: dismissed } = useTranslation('Dismissed');
  const { translatedText: searchPlaceholder } = useTranslation('Search reports...');
  const { translatedText: noReports } = useTranslation('No reports found');
  const { translatedText: noReportsDesc } = useTranslation('There are no reports matching your criteria');
  const { translatedText: view } = useTranslation('View');
  const { translatedText: reportDetails } = useTranslation('Report Details');
  const { translatedText: reportInformation } = useTranslation('Report Information');
  const { translatedText: type } = useTranslation('Type');
  const { translatedText: category } = useTranslation('Category');
  const { translatedText: severity } = useTranslation('Severity');
  const { translatedText: priority } = useTranslation('Priority');
  const { translatedText: status } = useTranslation('Status');
  const { translatedText: created } = useTranslation('Created');
  const { translatedText: description } = useTranslation('Description');
  const { translatedText: stepsToReproduce } = useTranslation('Steps to Reproduce');
  const { translatedText: evidence } = useTranslation('Evidence');
  const { translatedText: communication } = useTranslation('Communication');
  const { translatedText: resolution } = useTranslation('Resolution');
  const { translatedText: addResponse } = useTranslation('Add Response');
  const { translatedText: responsePlaceholder } = useTranslation('Write your response or resolution notes...');
  const { translatedText: assignToMe } = useTranslation('Assign to Me');
  const { translatedText: resolveReportLabel } = useTranslation('Resolve Report');
  const { translatedText: dismissReportLabel } = useTranslation('Dismiss');
  const { translatedText: loadingReports } = useTranslation('Loading reports...');
  const { translatedText: reportAssigned } = useTranslation('Report assigned to you');
  const { translatedText: reportResolved } = useTranslation('Report resolved successfully');
  const { translatedText: reportDismissed } = useTranslation('Report dismissed');
  const { translatedText: unknown } = useTranslation('Unknown');
  const { translatedText: nA } = useTranslation('N/A');
  const { translatedText: resolvedAt } = useTranslation('Resolved At');
  const { translatedText: escalate } = useTranslation('Escalate');
  const { translatedText: provider } = useTranslation('Provider');

  // Fetch reports on component mount and filter changes
  useEffect(() => {
    fetchReports();
    fetchStatistics();
  }, [filter, currentPage]);

  // Handle search with debounce
  useEffect(() => {
    const timer = setTimeout(() => {
      if (searchTerm.length >= 2 || searchTerm.length === 0) {
        fetchReports();
      }
    }, 500);

    return () => clearTimeout(timer);
  }, [searchTerm]);

  // Clear errors on unmount
  useEffect(() => {
    return () => {
      dispatch(clearReportErrors());
      dispatch(clearReportSuccess());
    };
  }, [dispatch]);

  const fetchReports = () => {
    const params = {
      page: currentPage,
      limit: limit,
      sortBy: 'createdAt',
      sortOrder: 'desc',
      ...(filter !== 'all' && { status: filter }),
    };

    if (searchTerm) {
      params.search = searchTerm;
    }

    dispatch(setReportFilters({ 
      status: filter !== 'all' ? filter : null, 
      search: searchTerm || null 
    }));
    dispatch(getAllReports(params));
  };

  const fetchStatistics = () => {
    dispatch(getReportStatistics());
    dispatch(getUrgentCount());
  };

  /**
   * Get admin ID from adminSlice
   */
  const getAdminId = () => {
    if (!currentUser) {
      console.warn('[AdminReports] No current user found in adminSlice');
      return null;
    }
    
    const adminId = currentUser._id || currentUser.id;
    
    if (!adminId) {
      console.warn('[AdminReports] Admin ID not found in user object:', currentUser);
      return null;
    }
    
    console.log('[AdminReports] ✅ Using admin ID from adminSlice:', adminId);
    return adminId.toString ? adminId.toString() : String(adminId);
  };

  const handleAssign = async (reportId) => {
    if (isProcessing) return;
    
    const adminId = getAdminId();
    
    if (!adminId) {
      toast.error('Unable to get admin ID. Please log in again.');
      return;
    }
    
    setIsProcessing(true);
    
    try {
      await dispatch(assignReportToAdmin({ reportId, adminId })).unwrap();
      
      toast.success(reportAssigned || 'Report assigned to you');
      
      // ✅ Close the modal
      setShowDetails(false);
      setSelectedReport(null);
      
      // ✅ Refresh the reports list
      await fetchReports();
      await fetchStatistics();
      
    } catch (err) {
      toast.error(err || 'Failed to assign report');
    } finally {
      setIsProcessing(false);
    }
  };

  const handleResolve = async (reportId) => {
    if (isProcessing) return;
    
    if (!responseMessage) {
      toast.error('Please add a resolution message');
      return;
    }

    const adminId = getAdminId();
    
    if (!adminId) {
      toast.error('Unable to get admin ID. Please log in again.');
      return;
    }

    setIsProcessing(true);

    try {
      await dispatch(resolveReport({ 
        reportId, 
        actionTaken: 'resolved',
        description: responseMessage,
        adminId
      })).unwrap();
      
      toast.success(reportResolved || 'Report resolved successfully');
      setResponseMessage('');
      setShowDetails(false);
      setSelectedReport(null);
      await fetchReports();
      await fetchStatistics();
      
    } catch (err) {
      toast.error(err || 'Failed to resolve report');
    } finally {
      setIsProcessing(false);
    }
  };

  const handleDismiss = async (reportId) => {
    if (isProcessing) return;
    
    const reason = responseMessage || 'Dismissed by admin';
    
    const adminId = getAdminId();
    
    if (!adminId) {
      toast.error('Unable to get admin ID. Please log in again.');
      return;
    }
    
    setIsProcessing(true);

    try {
      await dispatch(dismissReport({ reportId, reason, adminId })).unwrap();
      
      toast.success(reportDismissed || 'Report dismissed');
      setResponseMessage('');
      setShowDetails(false);
      setSelectedReport(null);
      await fetchReports();
      await fetchStatistics();
      
    } catch (err) {
      toast.error(err || 'Failed to dismiss report');
    } finally {
      setIsProcessing(false);
    }
  };

  const handleEscalate = async (reportId) => {
    if (isProcessing) return;
    
    const reason = responseMessage || 'Escalated to super admin for review';
    
    const adminId = getAdminId();
    
    if (!adminId) {
      toast.error('Unable to get admin ID. Please log in again.');
      return;
    }
    
    setIsProcessing(true);

    try {
      await dispatch(escalateReport({ reportId, reason, adminId })).unwrap();
      
      toast.success('Report escalated successfully');
      setResponseMessage('');
      setShowDetails(false);
      setSelectedReport(null);
      await fetchReports();
      await fetchStatistics();
      
    } catch (err) {
      toast.error(err || 'Failed to escalate report');
    } finally {
      setIsProcessing(false);
    }
  };

  const handleAddResponse = async (reportId) => {
    if (isProcessing) return;
    
    if (!responseMessage) {
      toast.error('Please add a response message');
      return;
    }

    const adminId = getAdminId();
    
    if (!adminId) {
      toast.error('Unable to get admin ID. Please log in again.');
      return;
    }

    setIsProcessing(true);

    try {
      await dispatch(addAdminResponse({ 
        reportId, 
        message: responseMessage,
        actionTaken: 'responded',
        adminId
      })).unwrap();
      
      toast.success('Response added successfully');
      setResponseMessage('');
      setShowDetails(false);
      setSelectedReport(null);
      await fetchReports();
      
    } catch (err) {
      toast.error(err || 'Failed to add response');
    } finally {
      setIsProcessing(false);
    }
  };

  const viewDetails = (reportId) => {
    // ✅ Don't open modal if already processing
    if (isProcessing) return;
    
    dispatch(getReportById(reportId))
      .unwrap()
      .then((response) => {
        const reportData = response?.data || response;
        setSelectedReport(reportData);
        setShowDetails(true);
        setResponseMessage('');
        setActionType('response');
      })
      .catch((err) => {
        toast.error(err || 'Failed to load report details');
      });
  };

  const closeModal = () => {
    if (isProcessing) return;
    setShowDetails(false);
    setSelectedReport(null);
    setResponseMessage('');
  };

  const getPriorityBadge = (priority) => {
    const priorityMap = {
      urgent: { label: 'Urgent', className: styles.badgeUrgent },
      critical: { label: 'Critical', className: styles.badgeUrgent },
      high: { label: 'High', className: styles.badgeHigh },
      medium: { label: 'Medium', className: styles.badgeMedium },
      low: { label: 'Low', className: styles.badgeLow },
    };

    const p = priorityMap[priority?.toLowerCase()];
    if (!p) return null;

    return (
      <span className={p.className}>
        {priority === 'urgent' || priority === 'critical' ? <AlertTriangle size={14} /> : null}
        {p.label}
      </span>
    );
  };

  const getStatusBadge = (status) => {
    const statusMap = {
      pending: { label: 'Pending', className: styles.badgePending, icon: <Clock size={14} /> },
      under_review: { label: 'Under Review', className: styles.badgeReview, icon: null },
      in_progress: { label: 'In Progress', className: styles.badgeProgress, icon: null },
      resolved: { label: 'Resolved', className: styles.badgeResolved, icon: <CheckCircle size={14} /> },
      dismissed: { label: 'Dismissed', className: styles.badgeDismissed, icon: <XCircle size={14} /> },
    };

    const s = statusMap[status];
    if (!s) return null;

    return (
      <span className={s.className}>
        {s.icon}
        {s.label}
      </span>
    );
  };

  const filterOptions = [
    { value: 'all', label: allReports || 'All Reports' },
    { value: 'pending', label: pending || 'Pending' },
    { value: 'under_review', label: underReview || 'Under Review' },
    { value: 'in_progress', label: inProgress || 'In Progress' },
    { value: 'resolved', label: resolved || 'Resolved' },
    { value: 'dismissed', label: dismissed || 'Dismissed' }
  ];

  useEffect(() => {
    if (error) {
      toast.error(error);
      dispatch(clearReportErrors());
    }
  }, [error, dispatch]);

  const getStatusCount = (statusKey) => {
    if (!statistics) return 0;

    const stats = statistics;
    
    switch (statusKey) {
      case 'pending':
        return Number(stats.pending || stats.pendingCount || 0);
      case 'under_review':
        return Number(stats.underReview || stats.underReviewCount || 0);
      case 'in_progress':
        return Number(stats.inProgress || stats.inProgressCount || 0);
      case 'resolved':
        return Number(stats.resolved || stats.resolvedCount || 0);
      case 'dismissed':
        return Number(stats.dismissed || stats.dismissedCount || 0);
      default:
        return 0;
    }
  };

  const getTotalCount = () => {
    if (!statistics) return 0;
    
    return Number(
      statistics.total ??
      statistics.totalCount ??
      statistics.totalReports ??
      statistics.count ??
      Object.values(statistics).reduce((sum, val) => {
        if (typeof val === 'number' && val >= 0) {
          return sum + val;
        }
        return sum;
      }, 0) ??
      0
    );
  };

  if (loading && reports.length === 0) {
    return (
      <div className={styles.loadingContainer}>
        <div className={styles.spinner}></div>
        <p>{loadingReports || 'Loading reports...'}</p>
      </div>
    );
  }

  return (
    <div className={styles.reportsContainer}>
      {/* Header */}
      <div className={styles.sectionHeader}>
        <div className={styles.headerLeft}>
          <h2>{reportsLabel || 'Reports'}</h2>
          <div className={styles.statsBadges}>
            <span className={styles.statBadge}>
              {total || 'Total'}: {getTotalCount()}
            </span>
            <span className={styles.statBadgePending}>
              {pending || 'Pending'}: {getStatusCount('pending')}
            </span>
            <span className={styles.statBadgeProgress}>
              {inProgress || 'In Progress'}: {getStatusCount('in_progress')}
            </span>
            <span className={styles.statBadgeResolved}>
              {resolved || 'Resolved'}: {getStatusCount('resolved')}
            </span>
            {urgentCount > 0 && (
              <span className={styles.statBadgeUrgent}>
                {urgent || 'Urgent'}: {urgentCount}
              </span>
            )}
          </div>
        </div>
        <button 
          className={styles.refreshBtn} 
          onClick={() => {
            fetchReports();
            fetchStatistics();
          }}
          disabled={loading}
        >
          <RefreshCw size={18} className={loading ? styles.spinning : ''} />
          {refresh || 'Refresh'}
        </button>
      </div>

      {/* Filters */}
      <div className={styles.filterSection}>
        <div className={styles.filterTabs}>
          {filterOptions.map((option) => (
            <button
              key={option.value}
              className={`${styles.filterTab} ${filter === option.value ? styles.active : ''}`}
              onClick={() => setFilter(option.value)}
            >
              {option.label}
            </button>
          ))}
        </div>
        <div className={styles.searchBox}>
          <Search size={18} />
          <input
            type="text"
            placeholder={searchPlaceholder || 'Search reports...'}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
      </div>

      {/* Reports List */}
      <div className={styles.reportsList}>
        {reports.length === 0 ? (
          <div className={styles.emptyState}>
            <Flag size={48} />
            <h3>{noReports || 'No reports found'}</h3>
            <p>{noReportsDesc || 'There are no reports matching your criteria'}</p>
          </div>
        ) : (
          reports.map((report) => {
            const reportData = report?.data || report;
            
            return (
              <div key={reportData._id || reportData.id || Math.random()} className={styles.reportCard}>
                <div className={styles.cardHeader}>
                  <div className={styles.reportInfo}>
                    <div className={styles.reportIcon}>
                      <Flag size={20} />
                    </div>
                    <div className={styles.info}>
                      <h4>{reportData.subject || 'No Subject'}</h4>
                      <p className={styles.reportType}>
                        {reportData.type?.replace('_', ' ').toUpperCase() || 'REPORT'}
                      </p>
                      <div className={styles.metaTags}>
                        <span className={styles.metaTag}>
                          <User size={14} />
                          {reportData.reportedBy?.name || 
                           reportData.reportedBy?.email || 
                           reportData.providerDetails?.businessName ||
                           unknown || 'Unknown'}
                        </span>
                        <span className={styles.metaTag}>
                          <Calendar size={14} />
                          {reportData.createdAt ? new Date(reportData.createdAt).toLocaleDateString() : 'N/A'}
                        </span>
                        {reportData.providerDetails?.businessName && (
                          <span className={styles.metaTag}>
                            {reportData.providerDetails.businessName}
                          </span>
                        )}
                        {reportData.assignedTo && (
                          <span className={styles.metaTag}>
                            Assigned to: {typeof reportData.assignedTo === 'object' 
                              ? reportData.assignedTo.name || reportData.assignedTo.email 
                              : reportData.assignedTo}
                          </span>
                        )}
                        {reportData.isEscalated && (
                          <span className={styles.metaTagEscalated}>
                            Escalated
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className={styles.cardActions}>
                    {getPriorityBadge(reportData.priority || reportData.severity)}
                    {getStatusBadge(reportData.status)}
                    <button 
                      className={styles.viewBtn}
                      onClick={() => viewDetails(reportData._id || reportData.id)}
                      disabled={isProcessing}
                    >
                      <Eye size={18} />
                      {view || 'View'}
                    </button>
                  </div>
                </div>
                {reportData.communication && reportData.communication.length > 0 && (
                  <div className={styles.cardFooter}>
                    <MessageCircle size={14} />
                    <span>{reportData.communication.length} messages</span>
                  </div>
                )}
              </div>
            );
          })
        )}
      </div>

      {/* Pagination */}
      {pagination && pagination.pages > 1 && (
        <div className={styles.pagination}>
          <button
            onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
            disabled={currentPage === 1 || loading || isProcessing}
          >
            Previous
          </button>
          <span>
            Page {currentPage} of {pagination.pages || 1}
          </span>
          <button
            onClick={() => setCurrentPage(prev => Math.min(prev + 1, pagination.pages || 1))}
            disabled={currentPage === (pagination.pages || 1) || loading || isProcessing}
          >
            Next
          </button>
        </div>
      )}

      {/* Details Modal */}
      {showDetails && selectedReport && (
        <div className={styles.modalOverlay} onClick={closeModal}>
          <div className={styles.modalContent} onClick={(e) => e.stopPropagation()}>
            <div className={styles.modalHeader}>
              <h3>{reportDetails || 'Report Details'}</h3>
              <button 
                className={styles.closeBtn} 
                onClick={closeModal}
                disabled={isProcessing}
              >
                <XCircle size={24} />
              </button>
            </div>
            <div className={styles.modalBody}>
              <div className={styles.detailSection}>
                <h4>{reportInformation || 'Report Information'}</h4>
                <div className={styles.detailGrid}>
                  <div className={styles.detailItem}>
                    <label>{type || 'Type'}</label>
                    <p>{selectedReport.type?.replace('_', ' ').toUpperCase() || nA}</p>
                  </div>
                  <div className={styles.detailItem}>
                    <label>{category || 'Category'}</label>
                    <p>{selectedReport.category || nA}</p>
                  </div>
                  <div className={styles.detailItem}>
                    <label>{severity || 'Severity'}</label>
                    <p className={styles.severityText}>{selectedReport.severity || nA}</p>
                  </div>
                  <div className={styles.detailItem}>
                    <label>{priority || 'Priority'}</label>
                    <p>{selectedReport.priority || nA}</p>
                  </div>
                  <div className={styles.detailItem}>
                    <label>{status || 'Status'}</label>
                    <p>{selectedReport.status || nA}</p>
                  </div>
                  <div className={styles.detailItem}>
                    <label>{created || 'Created'}</label>
                    <p>{new Date(selectedReport.createdAt).toLocaleString()}</p>
                  </div>
                  {selectedReport.providerDetails?.businessName && (
                    <div className={styles.detailItem}>
                      <label>{provider || 'Provider'}</label>
                      <p>{selectedReport.providerDetails.businessName}</p>
                    </div>
                  )}
                  {selectedReport.isEscalated && (
                    <div className={styles.detailItem}>
                      <label>Escalated</label>
                      <p>Yes</p>
                    </div>
                  )}
                </div>
              </div>

              <div className={styles.detailSection}>
                <h4>{description || 'Description'}</h4>
                <p className={styles.description}>{selectedReport.description || nA}</p>
              </div>

              {selectedReport.stepsToReproduce && selectedReport.stepsToReproduce.length > 0 && (
                <div className={styles.detailSection}>
                  <h4>{stepsToReproduce || 'Steps to Reproduce'}</h4>
                  <ol className={styles.stepsList}>
                    {selectedReport.stepsToReproduce.map((step, index) => (
                      <li key={index}>{step}</li>
                    ))}
                  </ol>
                </div>
              )}

              {selectedReport.evidence && selectedReport.evidence.length > 0 && (
                <div className={styles.detailSection}>
                  <h4>{evidence || 'Evidence'}</h4>
                  <div className={styles.evidenceList}>
                    {selectedReport.evidence.map((item, index) => (
                      <a 
                        key={index} 
                        href={item.url} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className={styles.evidenceItem}
                      >
                        <FileText size={16} />
                        {item.fileName || `Evidence ${index + 1}`}
                      </a>
                    ))}
                  </div>
                </div>
              )}

              {selectedReport.communication && selectedReport.communication.length > 0 && (
                <div className={styles.detailSection}>
                  <h4>{communication || 'Communication'}</h4>
                  <div className={styles.communicationList}>
                    {selectedReport.communication.map((msg, index) => (
                      <div key={index} className={styles.communicationItem}>
                        <span className={msg.from === 'admin' ? styles.adminMsg : styles.providerMsg}>
                          {msg.from}: {msg.message}
                        </span>
                        <span className={styles.msgTime}>
                          {new Date(msg.sentAt).toLocaleString()}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {selectedReport.resolution && (
                <div className={styles.detailSection}>
                  <h4>{resolution || 'Resolution'}</h4>
                  <div className={styles.resolutionInfo}>
                    <p><strong>{status || 'Status'}:</strong> {selectedReport.resolution.status}</p>
                    <p><strong>{description || 'Description'}:</strong> {selectedReport.resolution.description || nA}</p>
                    {selectedReport.resolution.resolvedAt && (
                      <p><strong>{resolvedAt || 'Resolved At'}:</strong> {new Date(selectedReport.resolution.resolvedAt).toLocaleString()}</p>
                    )}
                  </div>
                </div>
              )}

              {/* Response Section */}
              {selectedReport.status !== 'resolved' && selectedReport.status !== 'dismissed' && (
                <div className={styles.detailSection}>
                  <h4>{addResponse || 'Add Response'}</h4>
                  <div className={styles.actionTypeSelector}>
                    <select 
                      value={actionType} 
                      onChange={(e) => setActionType(e.target.value)}
                      className={styles.actionSelect}
                      disabled={isProcessing}
                    >
                      <option value="response">Add Response</option>
                      <option value="resolve">Resolve Report</option>
                      <option value="dismiss">Dismiss Report</option>
                      <option value="escalate">Escalate Report</option>
                    </select>
                  </div>
                  <textarea
                    className={styles.responseTextarea}
                    placeholder={responsePlaceholder || 'Write your response or resolution notes...'}
                    value={responseMessage}
                    onChange={(e) => setResponseMessage(e.target.value)}
                    rows={4}
                    disabled={isProcessing}
                  />
                </div>
              )}
            </div>

            <div className={styles.modalFooter}>
              {selectedReport.status === 'pending' && (
                <button 
                  className={styles.assignBtn}
                  onClick={() => handleAssign(selectedReport._id || selectedReport.id)}
                  disabled={isProcessing || updating}
                >
                  {isProcessing ? (
                    <span className={styles.spinnerSmall}></span>
                  ) : (
                    <User size={18} />
                  )}
                  {assignToMe || 'Assign to Me'}
                </button>
              )}
              {(selectedReport.status === 'under_review' || selectedReport.status === 'in_progress') && (
                <>
                  <button 
                    className={styles.responseBtn}
                    onClick={() => handleAddResponse(selectedReport._id || selectedReport.id)}
                    disabled={!responseMessage || isProcessing || updating}
                  >
                    {isProcessing ? (
                      <span className={styles.spinnerSmall}></span>
                    ) : (
                      <MessageCircle size={18} />
                    )}
                    Send Response
                  </button>
                  <button 
                    className={styles.resolveBtn}
                    onClick={() => handleResolve(selectedReport._id || selectedReport.id)}
                    disabled={!responseMessage || isProcessing || updating}
                  >
                    {isProcessing ? (
                      <span className={styles.spinnerSmall}></span>
                    ) : (
                      <CheckCircle size={18} />
                    )}
                    {resolveReportLabel || 'Resolve Report'}
                  </button>
                  <button 
                    className={styles.dismissBtn}
                    onClick={() => handleDismiss(selectedReport._id || selectedReport.id)}
                    disabled={isProcessing || updating}
                  >
                    {isProcessing ? (
                      <span className={styles.spinnerSmall}></span>
                    ) : (
                      <XCircle size={18} />
                    )}
                    {dismissReportLabel || 'Dismiss'}
                  </button>
                  {!selectedReport.isEscalated && (
                    <button 
                      className={styles.escalateBtn}
                      onClick={() => handleEscalate(selectedReport._id || selectedReport.id)}
                      disabled={isProcessing || updating}
                    >
                      {isProcessing ? (
                        <span className={styles.spinnerSmall}></span>
                      ) : (
                        <AlertTriangle size={18} />
                      )}
                      {escalate || 'Escalate'}
                    </button>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminReports;